/**
 * Operations · Overview.
 */
import { Sparkles, Factory, ShieldAlert } from "lucide-react";
import { Card, SectionHeader, DeltaChip, SeverityBadge } from "../../components/lib";
import { ListRow } from "../../components/lib.overlay";
import { OPERATIONS_OVERVIEW, BATCHES, RISKS } from "../../mocks/operationsData";
import { useNavigate } from "react-router-dom";

export default function Overview() {
  const navigate = useNavigate();
  const inFlight = BATCHES.filter((b) => b.stage !== "done");
  const openRisks = RISKS.filter((r) => r.status !== "mitigated").sort((a, b) => (a.severity === "high" ? -1 : 1));

  return (
    <div className="space-y-6" data-testid="op-overview">
      <Card testId="op-kpis">
        <SectionHeader eyebrow="Snapshot" title="Operations Performance" action={<Factory size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {OPERATIONS_OVERVIEW.kpis.map((k) => (
            <div key={k.key} data-testid={`op-kpi-${k.key}`}>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-ink-muted)" }}>{k.label}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)", letterSpacing: "-0.01em" }}>{k.value}</div>
              <div className="mt-1 flex items-center gap-2">
                <DeltaChip delta={k.delta} trend={k.trend as any} />
                {k.hint && <span className="text-[10px]" style={{ color: "var(--ph-ink-soft)" }}>{k.hint}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card testId="op-inflight" className="lg:col-span-2">
          <SectionHeader
            eyebrow="Production floor"
            title="Batches in flight"
            action={
              <button
                onClick={() => navigate("/pureh/operations/batches")}
                className="text-xs font-medium"
                style={{ color: "var(--ph-brand-primary)" }}
                data-testid="op-open-batches"
              >
                Open queue →
              </button>
            }
          />
          <div className="space-y-3">
            {inFlight.map((b) => (
              <div key={b.id} data-testid={`op-inflight-${b.id}`}
                className="flex items-center gap-3 p-3"
                style={{ border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
                <div className="text-[10px] font-mono flex-shrink-0" style={{ color: "var(--ph-ink-muted)" }}>{b.code}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{b.productName}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>
                    {b.stage} · {b.units} units · started {b.startedRelative} · ETA {b.etaRelative}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{Math.round(b.yieldPct * 100)}%</div>
                  <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Yield</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card testId="op-risks">
          <SectionHeader
            eyebrow="Risk register"
            title="Open risks"
            action={<ShieldAlert size={14} style={{ color: "var(--ph-ink-muted)" }} />}
          />
          <div className="space-y-1">
            {openRisks.slice(0, 4).map((r) => (
              <ListRow
                key={r.id}
                testId={`op-risk-mini-${r.id}`}
                onClick={() => navigate("/pureh/operations/risks")}
                label={r.title}
                sublabel={<>Owner {r.owner} · <span style={{ color: "var(--ph-ai)" }}>AI {Math.round(r.aiConfidence * 100)}%</span></>}
                trailing={<SeverityBadge severity={r.severity} />}
              />
            ))}
          </div>
        </Card>
      </div>

      <Card testId="op-ai">
        <SectionHeader eyebrow="AI" title="What Ops AI is watching" action={<Sparkles size={14} style={{ color: "var(--ph-ai)" }} />} />
        {(OPERATIONS_OVERVIEW.aiRisks?.length || 0) === 0 && (OPERATIONS_OVERVIEW.aiOpportunities?.length || 0) === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: "var(--ph-ink-muted)" }} data-testid="op-ai-empty">
            No operations signals yet. AI will surface risks and opportunities once batches, QC checks, and supplier orders are flowing.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(OPERATIONS_OVERVIEW.aiRisks || []).map((r: { id: string; title: string; body: string }) => (
              <div key={r.id} data-testid={`op-ai-risk-${r.id}`} className="p-3" style={{ background: "var(--ph-danger-tint)", borderRadius: "var(--ph-radius-md)" }}>
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--ph-danger)" }}>Risk</div>
                <div className="text-sm font-semibold mt-1" style={{ color: "var(--ph-ink-strong)" }}>{r.title}</div>
                <div className="text-xs mt-1" style={{ color: "var(--ph-ink-default)" }}>{r.body}</div>
              </div>
            ))}
            {(OPERATIONS_OVERVIEW.aiOpportunities || []).map((o: { id: string; title: string; body: string }) => (
              <div key={o.id} data-testid={`op-ai-opp-${o.id}`} className="p-3" style={{ background: "var(--ph-success-tint)", borderRadius: "var(--ph-radius-md)" }}>
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--ph-success)" }}>Opportunity</div>
                <div className="text-sm font-semibold mt-1" style={{ color: "var(--ph-ink-strong)" }}>{o.title}</div>
                <div className="text-xs mt-1" style={{ color: "var(--ph-ink-default)" }}>{o.body}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
